import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Button } from '../../components/ui/Button'
import { EmptyState } from '../../components/ui/EmptyState'
import { Skeleton } from '../../components/ui/Skeleton'
import { facetSearchParams, parseFacetSelection } from './catalog-series-facet-filter'
import { OTHER_GROUP_NAME, usesCardConfiguratorLayout } from './catalogTaxonomy'
import { CatalogSeriesFacetSidebar } from './components/CatalogSeriesFacetSidebar'
import { SeriesCard } from './components/SeriesCard'
import { SeriesConfiguratorDrawer } from './components/SeriesConfiguratorDrawer'
import type { CatalogSeries } from './types'
import { defaultCatalogFacetsService, useCatalogFacets } from './useCatalogFacets'
import type { CatalogFacetsService } from './useCatalogFacets'
import { defaultCatalogSearchService } from './useCatalogSearch'
import { useCatalogSeries } from './useCatalogSeries'
import { useCategoryTaxonomy } from './useCategoryTaxonomy'
import type { CategoryListingService } from './useCategoryTaxonomy'

export interface CatalogSeriesPageProps {
  /** Injectable for tests; defaults to the real BFF-backed
   * `GET /catalog/categories/{category}/series`. UID-387 — this route now
   * accepts the facet `selection` directly (existential narrowing
   * server-side), so no second search-and-intersect service is needed here
   * any more. */
  readonly service?: Parameters<typeof useCatalogSeries>[2]
  /** Injectable for tests; defaults to the real BFF-backed `GET /catalog/categories`,
   * the same read `CatalogBrowsePage`'s "Shop by category" groups against. Backs the
   * breadcrumb's taxonomy-group segment and the sibling-category chip row below —
   * both real data, not a stand-in for the still-deferred filter sidebar (ADR-0099
   * Gap 1). */
  readonly categoryService?: CategoryListingService
  /** Injectable for tests; defaults to the real BFF-backed
   * `GET /catalog/categories/{category}/facets` (UID-385 / ADR-0121). Backs the
   * left-hand facet sidebar (ADR-0120 D8 items 5/7). */
  readonly facetsService?: CatalogFacetsService
}

/**
 * The series-card grid (UID-286) — `Fictiv Platform.dc.html`'s
 * `catStage==='selector'` screen, scoped to one leaf category. Reached by
 * clicking a leaf-category chip inside a "Shop by category" group tile
 * (`CatalogBrowsePage`/`ShopByCategory`); this REPLACES that chip's prior
 * "not built yet" toast, per the architect's flow reconciliation
 * (`docs/design/catalog-configurator-surfaces-reconciliation.md` §3): the
 * chip's real destination is this series grid, not a listing screen.
 *
 * Deliberately omitted vs. the mock's full selector screen (ADR-0099 Gap 1,
 * 2026-08-25 — supersedes the earlier reconciliation doc's open question):
 * the left filter sidebar (Stock Availability, Metric/Inch, Standard/
 * Configurable, per-dimension facets) and the "Filter by End Type" swatch
 * row. Neither is backed by any real data source (`kind` is the only real
 * field and maps poorly onto series-card granularity) — building either now
 * would be exactly the kind of unbacked stub ADR-0090/0091/0099 reject.
 * ADR-0099 names a concrete blocking prerequisite (a data-engineering
 * facet-ingestion ticket + UID-38) and a follow-up ticket (UID-34b) for when
 * that lands.
 *
 * What IS real and now built, closing part of the visual gap honestly: the
 * breadcrumb's middle taxonomy-GROUP segment ("Catalog / Linear Motion /
 * Linear Shafts", not just "Catalog / Linear Shafts"), a chip row for the
 * group's other real sibling leaf categories (quick lateral navigation,
 * mirroring the design's category-pill row), a real series-count subheading,
 * and a grid/list view toggle (pure presentation, no data dependency). Its
 * `.view-toggle-fieldset`/`.view-toggle-btn` styling was originally authored
 * for `AllWorkspacesPage.tsx` and reused as-is rather than reinvented; UID-382
 * then gave that page an icon-only pill per its own comp, so this page is now
 * the sole consumer and the rules live in `catalog.css` (not workspaces.css).
 *
 * NOTE (ADR-0111, correcting ADR-0099 Gap 1): "neither is backed by any real
 * data source" is no longer true across the board. Material, Finish and
 * Metric/Inch ARE real per-item facets on `configAttributes.specs` (~60% of
 * seeded items). This sidebar stays deferred anyway, on grounds more data
 * cannot fix (ADR-0111 D5): this screen is a grid of SERIES cards while
 * `specs` is a per-ITEM attribute, so aggregating up reproduces the half-true
 * control ADR-0099 already rejected for `kind`; and a sidebar present on 3 of
 * 16 categories and silently absent on the other 13 is its own coherence
 * defect (ADR-0091 D3 / ADR-0110). The concept is re-homed onto the
 * item-granular "View all N items in this category" grid (ADR-0110 D3).
 * Genuinely unbacked, by contrast: End Type / End Machining (absent from all
 * 109 spec names) and real Stock Availability (still needs UID-38).
 *
 * ADR-0120 D8 item 1 — the series-card GRID (`.catalog-series-grid`, the
 * default `view === 'grid'` layout above, distinct from the single-column
 * `.catalog-series-list` the toggle switches to) is a fixed 2-column layout,
 * not the previous fluid `auto-fill` grid, to leave room for the left-hand
 * filter sidebar.
 *
 * ADR-0120 D8 items 5/7 + ADR-0121 (UID-385), now on the UID-387 existential
 * live-count model (frontend
 * `claude-feature-notes/2026-08-28-existential-live-count-facet-model.md`):
 * `useCatalogFacets` fetches the facet catalogue for `category` — the
 * precomputed, query-independent one when no facet is selected, gated to the
 * 100%-series-coverage floor (`isSeriesFacetRenderable`); a LIVE drilldown
 * scoped to `selection` once at least one facet IS selected, gated only to
 * `values.length >= 2`, no coverage floor. The sidebar is omitted entirely,
 * not rendered empty, when no facet clears the gate for the current
 * selection — an absent element for "no filters here" is the honest state
 * ADR-0120 D4 calls for, not a labelled-empty sidebar nobody asked to see.
 *
 * The series-list endpoint itself (`GET /catalog/categories/{category}/series`)
 * now accepts the SAME `specs[<name>]=<value>` selection (UID-387) and
 * narrows EXISTENTIALLY server-side — a series is returned iff it contains at
 * least one item matching the whole selection. This retires the earlier
 * `useFilteredSeriesCodes.ts` item-search-and-intersect workaround (and its
 * disclosed 100-item-page cap) entirely: `useCatalogSeries` is called with
 * `selection` directly and its `series` are rendered as-is, already narrowed.
 * The selection itself lives in the URL (`specs[<name>]=<value>`, mirroring
 * the BFF's own param shape) so a filtered view is shareable/bookmarkable,
 * same as `CatalogSearchPage`'s `?q=`.
 *
 * "View types" (a card's CTA) navigates to `CatalogSeriesConfigurePage`
 * (Surface A, UID-34 / ADR-0097 §8.1) — the per-series configurator page.
 * That page itself decides, from the fetched card's `configSchema`, whether
 * to render a working picker, the honest disabled note (a non-resolvable/
 * multi-dimension family), or the plain non-configurable "Add" path; this
 * page never re-derives that gate, it only navigates.
 *
 * UID-460 step 2 exception: for the `usesCardConfiguratorLayout` allowlist
 * (Locating Pins / Locating Pins & Bushings), "View types" no longer
 * navigates anywhere — it opens `SeriesConfiguratorDrawer` inline instead,
 * collapsing the series-card click + `CatalogSeriesConfigurePage` card-grid
 * click into one. Every other category (including Linear Shafts) keeps the
 * navigate behavior above completely unchanged; that route itself is also
 * untouched and still reachable as a deep link/fallback.
 */
export function CatalogSeriesPage({
  service,
  categoryService,
  facetsService = defaultCatalogFacetsService,
}: CatalogSeriesPageProps) {
  const { category = '' } = useParams<{ category: string }>()
  const taxonomy = useCategoryTaxonomy(categoryService ?? defaultCatalogSearchService)
  const navigate = useNavigate()
  const [view, setView] = useState<'grid' | 'list'>('grid')
  const [searchParams, setSearchParams] = useSearchParams()
  // UID-460 step 2 — the series-card + drawer configurator pilot's inline
  // open state, distinct from `navigate`: only ever set for a category on
  // the `usesCardConfiguratorLayout` allowlist (see `handleOpen`).
  const [openSeries, setOpenSeries] = useState<CatalogSeries | null>(null)
  const useCardConfigurator = usesCardConfiguratorLayout(category)

  const selection = useMemo(() => parseFacetSelection(searchParams), [searchParams])
  const hasSelection = Object.keys(selection).length > 0
  // UID-416 / ADR-0130 D10: the series-grain `brand` filter, read straight
  // off the URL — a tile's `linkPath` lands here as
  // `/catalog/series/<category>?brand=<value>`. Blank is treated as absent,
  // same convention every other param on this page already follows.
  // `facetSearchParams` needs no change to preserve this across a facet
  // interaction: it only ever deletes `specs[*]` keys, so `brand` survives
  // by construction (verified, not assumed — see catalog-series-facet-filter.test.ts).
  const rawBrand = searchParams.get('brand')
  const brand = rawBrand && rawBrand.trim().length > 0 ? rawBrand : undefined
  // UID-387 — `selection` now narrows the series list existentially
  // server-side, so `state.series` is already the correct, narrowed set; no
  // client-side intersection against a second search call is needed.
  const state = useCatalogSeries(category, selection, service, brand)
  const facetsState = useCatalogFacets(category, selection, facetsService)

  function handleSelectionChange(next: Readonly<Record<string, string>>) {
    setSearchParams(facetSearchParams(searchParams, next))
  }

  const group =
    taxonomy.status === 'ready'
      ? taxonomy.groups.find((g) => g.categories.includes(category))
      : undefined
  const isOther = taxonomy.status === 'ready' && !group && taxonomy.other.includes(category)
  const groupLabel = group?.name ?? (isOther ? OTHER_GROUP_NAME : undefined)
  const siblingCategories = group?.categories ?? (isOther ? taxonomy.other : [])

  const displayedSeries = state.status === 'ready' ? state.series : []

  function handleOpen(series: CatalogSeries) {
    // UID-460 step 2 / ADR-0153 D7 — the allowlisted categories skip the
    // second navigation entirely and open the drawer inline, but ONLY for a
    // series whose reprocess has actually landed (`configuratorReady ===
    // true`). A series still missing that (or a category whose backend
    // opt-in hasn't caught up with this frontend allowlist yet) degrades
    // honestly to the SAME navigate fallback every other category uses,
    // rather than opening an empty/broken drawer. The carried-facet-
    // selection concern below doesn't apply to the drawer branch: it
    // configures ONE series' own axes, with no row-level dimension filter
    // of its own for a category-level facet pick to compose with.
    if (useCardConfigurator && series.configuratorReady) {
      setOpenSeries(series)
      return
    }
    // Carries the current facet `selection` into the configure page via the
    // SAME `specs[<name>]=<value>` URL convention this page already reads/
    // writes (`facetSearchParams`/`parseFacetSelection`) — a category-level
    // facet pick (e.g. `Shaft Style: Hollow`) otherwise vanished entirely on
    // "View types", landing on the series' full unfiltered row list. The
    // configure page parses this back out with the same `parseFacetSelection`
    // and narrows client-side against its own already-fetched rows (see
    // `catalog-carried-facet-filter.ts`) — no second fetch, no forcing this
    // onto the configure page's unrelated dimension-based filter.
    const carriedParams = facetSearchParams(new URLSearchParams(), selection)
    const query = carriedParams.toString()
    navigate(
      `/catalog/series/${encodeURIComponent(category)}/${encodeURIComponent(series.seriesCode)}${
        query ? `?${query}` : ''
      }`,
    )
  }

  return (
    <section className="catalog-storefront" aria-labelledby="catalog-series-heading">
      <div className="catalog-series-breadcrumb">
        <Link to="/catalog">Catalog</Link>
        <span aria-hidden="true">/</span>
        {groupLabel && (
          <>
            <span>{groupLabel}</span>
            <span aria-hidden="true">/</span>
          </>
        )}
        <span>{category}</span>
      </div>

      <div className="catalog-storefront-header">
        <div>
          <h1 id="catalog-series-heading" className="catalog-storefront-title">
            {category}
          </h1>
          {state.status === 'ready' && (
            <p className="catalog-storefront-subcount">{displayedSeries.length} series</p>
          )}
        </div>
        {state.status === 'ready' && state.series.length > 0 && (
          <fieldset className="view-toggle-fieldset">
            <legend className="sr-only">View</legend>
            <button
              type="button"
              aria-pressed={view === 'grid'}
              className={
                view === 'grid' ? 'view-toggle-btn view-toggle-btn-active' : 'view-toggle-btn'
              }
              onClick={() => setView('grid')}
            >
              Grid
            </button>
            <button
              type="button"
              aria-pressed={view === 'list'}
              className={
                view === 'list' ? 'view-toggle-btn view-toggle-btn-active' : 'view-toggle-btn'
              }
              onClick={() => setView('list')}
            >
              List
            </button>
          </fieldset>
        )}
      </div>

      {siblingCategories.length > 1 && (
        <div className="catalog-category-filter-bar" aria-label="Related categories">
          {siblingCategories.map((sibling) =>
            sibling === category ? (
              <span
                key={sibling}
                className="catalog-category-chip catalog-category-chip-active"
                aria-current="page"
              >
                {sibling}
              </span>
            ) : (
              <Link
                key={sibling}
                to={`/catalog/series/${encodeURIComponent(sibling)}`}
                className="catalog-category-chip"
              >
                {sibling}
              </Link>
            ),
          )}
        </div>
      )}

      <div className={facetsState.facets.length > 0 ? 'catalog-series-facet-layout' : undefined}>
        {facetsState.facets.length > 0 && (
          <CatalogSeriesFacetSidebar
            facets={facetsState.facets}
            selection={selection}
            onChange={handleSelectionChange}
            isPending={facetsState.status === 'loading'}
          />
        )}

        <div>
          {/* UID-387 — `state.status === 'loading'` fires on the INITIAL fetch
           * AND on every selection-change re-fetch (the live existential
           * narrowing a facet selection triggers has been measured up to
           * ~5.5s for a broad selection), so this skeleton is also this
           * page's visible pending state for a narrowing in flight, not only
           * the first paint. */}
          {state.status === 'loading' && (
            <output className="catalog-series-grid" aria-busy="true">
              <span className="sr-only">Loading “{category}” series…</span>
              {Array.from({ length: 6 }, (_, index) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: a fixed-count skeleton grid has no stable identity to key by.
                <Skeleton key={index} height={148} variant="block" />
              ))}
            </output>
          )}

          {state.status === 'error' && (
            <EmptyState
              title="We couldn’t load this category’s series"
              detail={state.message}
              action={<Button onClick={state.retry}>Try again</Button>}
            />
          )}

          {state.status === 'ready' && displayedSeries.length === 0 && (
            <EmptyState
              title={hasSelection ? 'No series match these filters' : 'No series to browse yet'}
              detail={
                hasSelection
                  ? 'Try clearing a filter to see more series.'
                  : `We don't have any series on file for "${category}" yet.`
              }
            />
          )}

          {state.status === 'ready' && displayedSeries.length > 0 && (
            <div className={view === 'grid' ? 'catalog-series-grid' : 'catalog-series-list'}>
              {displayedSeries.map((series) => (
                <SeriesCard key={series.seriesCode} series={series} onOpen={handleOpen} />
              ))}
            </div>
          )}
        </div>
      </div>

      {useCardConfigurator && openSeries && (
        <SeriesConfiguratorDrawer
          // Keyed on seriesCode so opening a DIFFERENT series while the
          // drawer is already mounted (rare, but not impossible via a fast
          // re-click) remounts fresh — resetting the drawer's own picks and
          // its stale-while-revalidate cache rather than carrying a
          // previous series' selections into a new one.
          key={openSeries.seriesCode}
          open
          onClose={() => setOpenSeries(null)}
          category={category}
          {...(groupLabel !== undefined ? { categoryGroup: groupLabel } : {})}
          seriesCode={openSeries.seriesCode}
          seriesName={openSeries.name}
          seriesImageUrl={openSeries.imageUrl}
          seriesDescription={openSeries.description}
        />
      )}
    </section>
  )
}
